import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { formatDateToYYYYMMDD } from '@/utils';
import type { LC_ValidationError } from '@/api/shared';

import { useCatalogLoader } from '@/hooks/ui/useCatalogLoader';
import { useUI } from '@/hooks/ui/useUI';
import { markingEventService } from '@/api/domains/catalog/eventType/eventType.service';
import type { MarkingEvent } from '@/api/domains/catalog/eventType/eventType.types';

// Each boolean controls whether the filter is rendered AND whether its API call is made
export interface SearchFilterConfig {
  dateRange?: boolean;
  events?: boolean;
  markStatus?: boolean;
}

export interface AttendanceSearchFiltersState {
  dateFrom: string;
  dateTo: string;
  eventIds: string[];
  markStatus: string; // '' | '0' | '1'
}

export interface AttendanceSearchFilterErrors {
  dateRange?: string;
  events?: string;
  markStatus?: string;
}

// Maps backend camelCase fieldNames to UI error keys
const FIELD_TO_ERROR_KEY: Record<string, keyof AttendanceSearchFilterErrors> = {
  dateFrom: 'dateRange',
  dateTo: 'dateRange',
  eventIds: 'events',
  status: 'markStatus',
};

// Date range constraints per ASIS-5339: no future dates, max 31-day span from the start date.
const MAX_DATE_RANGE_DAYS = 31;

export interface UseAttendanceSearchFiltersResult {
  filters: AttendanceSearchFiltersState;
  errors: AttendanceSearchFilterErrors;
  eventOptions: { value: string; label: string }[];
  markStatusOptions: { value: string; label: string }[];
  isLoadingEvents: boolean;
  maxDate: string;
  maxRangeDays: number;
  handlers: {
    onDateRangeChange: (range: { from?: string; to?: string }) => void;
    onEventsChange: (ids: string | string[]) => void;
    onMarkStatusChange: (value: string | string[]) => void;
  };
  injectErrors: (validationErrors: LC_ValidationError[]) => number;
  clearErrors: () => void;
}

function getDefaultDateRange(): { from: string; to: string } {
  const today = new Date();
  const oneMonthAgo = new Date(today);
  oneMonthAgo.setMonth(today.getMonth() - 1);

  return { from: formatDateToYYYYMMDD(oneMonthAgo), to: formatDateToYYYYMMDD(today) };
}

export function useAttendanceSearchFilters(
  config: SearchFilterConfig,
): UseAttendanceSearchFiltersResult {
  const { showNotification } = useUI();
  const { t: translate } = useTranslation();

  const defaultRange = getDefaultDateRange();
  const maxDate = useMemo(() => formatDateToYYYYMMDD(new Date()), []);

  const [filters, setFilters] = useState<AttendanceSearchFiltersState>({
    dateFrom: defaultRange.from,
    dateTo: defaultRange.to,
    eventIds: [],
    markStatus: '1',
  });

  const [errors, setErrors] = useState<AttendanceSearchFilterErrors>({});

  const eventsEnabled = config.events !== false;

  // Load marking events only when eventsEnabled
  const eventCatalog = useCatalogLoader<MarkingEvent>(
    (signal) => markingEventService.getMarkingEvents(signal),
    (event) => ({ value: String(event.id), label: event.description }),
    [],
    !eventsEnabled,
    () => showNotification('error', translate('common:errors.loadingData'), 5000),
  );

  const markStatusOptions = useMemo(
    () => [
      { value: '1', label: translate('attendance:searchFilters.enabled') },
      { value: '0', label: translate('attendance:searchFilters.disabled') },
    ],
    [translate],
  );

  const onDateRangeChange = useCallback((range: { from?: string; to?: string }) => {
    setFilters((prev) => ({
      ...prev,
      dateFrom: range.from ?? prev.dateFrom,
      dateTo: range.to ?? prev.dateTo,
    }));
    setErrors((prev) => ({ ...prev, dateRange: undefined }));
  }, []);

  const onEventsChange = useCallback((ids: string | string[]) => {
    const value = Array.isArray(ids) ? ids : ids ? [ids] : [];
    setFilters((prev) => ({ ...prev, eventIds: value }));
    setErrors((prev) => ({ ...prev, events: undefined }));
  }, []);

  const onMarkStatusChange = useCallback((value: string | string[]) => {
    const resolved = Array.isArray(value) ? value[0] ?? '' : value;
    setFilters((prev) => ({ ...prev, markStatus: resolved }));
    setErrors((prev) => ({ ...prev, markStatus: undefined }));
  }, []);

  // Injects backend validationErrors into the local error state.
  // Returns the count of errors that mapped to a visible field.
  const injectErrors = useCallback((validationErrors: LC_ValidationError[]): number => {
    const next: AttendanceSearchFilterErrors = {};
    let mappedCount = 0;

    validationErrors.forEach((error) => {
      const key = FIELD_TO_ERROR_KEY[error.fieldName];
      if (key) {
        // Keep first message per field if multiple errors map to the same key
        if (!next[key]) {
          next[key] = error.errorMessage;
        }
        mappedCount++;
      }
    });

    setErrors(next);
    return mappedCount;
  }, []);

  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  return {
    filters,
    errors,
    eventOptions: eventsEnabled ? eventCatalog.options : [],
    markStatusOptions,
    isLoadingEvents: !eventCatalog.isLoaded && eventsEnabled,
    maxDate,
    maxRangeDays: MAX_DATE_RANGE_DAYS,
    handlers: {
      onDateRangeChange,
      onEventsChange,
      onMarkStatusChange,
    },
    injectErrors,
    clearErrors,
  };
}
